import { Injectable } from "@angular/core";
import { ToastService } from "@core/components/toasts/toasts.service";

export type FileContentResponse = {
	error?: string;
	details?: Error;
	fileName: string;
	extension: string;
	encodedBase64: string;
};

@Injectable({
	providedIn: "root",
})
export class FileService {
	constructor(private toastService: ToastService) { }

	public base64ToBlob(b64Data, contentType = "", sliceSize = 512) {
		b64Data = b64Data.replace(/\s/g, "");
		const byteCharacters = atob(b64Data);
		const byteArrays = [];
		for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
			const slice = byteCharacters.slice(offset, offset + sliceSize);

			const byteNumbers = new Array(slice.length);
			for (let i = 0; i < slice.length; i++) {
				byteNumbers[i] = slice.charCodeAt(i);
			}
			const byteArray = new Uint8Array(byteNumbers);
			byteArrays.push(byteArray);
		}
		return new Blob(byteArrays, { type: contentType });
	}

	public saveFile(blob: Blob, fileName: string) {
		if (blob) {
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `RHGestao-${fileName}`;
			link.click();

			window.URL.revokeObjectURL(url);
		}
	}

	public saveZipFile(blob: Blob, fileName: string) {
		if (blob) {
			const url = window.URL.createObjectURL(blob);
			const link = document.createElement("a");
			link.href = url;
			link.download = `RHGestao-${fileName}.zip`;
			link.click();
			window.URL.revokeObjectURL(url);
		}
	}

	public openFile(res: Blob) {
		const downloadURL = window.URL.createObjectURL(res);
		window.open(downloadURL);
	}

	public async fileToBase64(file: File) {
		return new Promise((resolve, reject) => {
			const reader = new FileReader();

			reader.readAsDataURL(file);

			reader.onload = () => resolve(reader.result);
			reader.onerror = (error) => reject(error);
		});
	}

	public downloadBase64File(
		base64: string,
		contentType: string,
		fileName: string
	): void {
		const blob = this.base64ToBlob(base64, contentType);
		this.saveFile(blob, fileName);
	}

	public getFileContentByInputFile(file: File, allowedExtensions?: string[], maxSizeMb: number = 17): Promise<FileContentResponse> {
		return new Promise((resolve, reject) => {
			if (!file) {
				reject({ error: "Nenhum arquivo fornecido." });

				return;
			}

			const maxFileSizeInBytes = maxSizeMb * 1024 * 1024;
			if (file.size > maxFileSizeInBytes) {
				reject({ error: `O arquivo excede o tamanho máximo permitido de ${maxSizeMb}MB.` });
				this.toastService.showInfo(`O arquivo excede o tamanho máximo permitido de ${maxSizeMb}MB.`);
				return;
			}

			const reader = new FileReader();

			reader.onloadend = () => {
				const encodedBase64 = reader.result as string;

				const extension = file.name.split(".").pop();

				if (!!allowedExtensions && !allowedExtensions.includes(extension.toLowerCase())) {
					reject({ error: "O formato do arquivo inserido não é permitido." });

					return;
				}

				const base64String = encodedBase64.split("base64,")[1];

				resolve({
					extension,
					fileName: file.name,
					encodedBase64: base64String,
				});
			};

			reader.onerror = (error) => {
				reject({ error: "Erro ao ler o arquivo.", details: error });
			};

			reader.readAsDataURL(file);
		});
	}

	public getFileContent(
		file: File,
		allowedExtensions: string[]
	): Promise<{ encodedBase64: string; fileName: string; extension: string } | null> {
		return this.getFileContentByInputFile(file, allowedExtensions)
			.then((r) => ({
				encodedBase64: r.encodedBase64,
				fileName: r.fileName,
				extension: r.extension,
			}))
			.catch((err: { error?: string }) => {
				this.toastService.showWarning(
					err?.error ?? "O formato do arquivo inserido não é permitido."
				);
				return null;
			});
	}

	previewFileInNewWindow(
		contentType: string,
		content: string,
		fileName: string
	) {
		const newWindow = window.open("", "_blank");
		if (newWindow) {
			const isPdf = contentType === "application/pdf";
			const bodyContent = isPdf
				? `<embed src="data:${contentType};base64,${content}" type="${contentType}" width="100%" height="100%" />`
				: `<img src="data:${contentType};base64,${content}" alt="Imagem" style="max-width: 100%; height: auto;" />`;

			newWindow.document.write(`
			<html>
			  <head>
				<title>${fileName}</title>
			  </head>
			  <body>
				${bodyContent}
			  </body>
			</html>
		  `);
			newWindow.document.close();
		}
	}
}
